import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { get, post } from "helpers/api_helper"
import { store as socketStore } from "components/Common/Socket/store"
import { newPlayerOperationId } from "pages/UserAccounts/PlayerAccounts/playerInvitationState"

export const searchRebuildError = code => ({
  SEARCH_REBUILD_BUSY: "Another rebuild is already in progress. Follow its result before starting a new request.",
  SUPER_ADMIN_REQUIRED: "Your session no longer has active SuperAdmin access.",
  OPERATION_PAYLOAD_CONFLICT: "This request identifier belongs to a different intention. Check the original result.",
  INVALID_SEARCH_ACCOUNT_IDENTITY: "An account has an invalid search identity. Correct the data before rebuilding. The previous index was kept.",
  SEARCH_COVERAGE_MISMATCH: "The replacement did not include every expected account. The previous index was kept.",
  SEARCH_WRITES_UNCONFIRMED: "Some account updates could not be verified. The previous index was kept.",
  SEARCH_TASK_FAILED: "Search rejected a replacement task. The previous index was kept.",
  SEARCH_BUILD_FAILED: "The replacement could not be prepared. The previous index was kept.",
  SEARCH_ACTIVATION_FAILED: "Search rejected activation. The previous index was kept.",
  SEARCH_ACTIVATION_FENCED: "Activation was interrupted and the replacement was retired safely. The previous index was kept. You can review a new rebuild.",
}[code] || "The rebuild could not be confirmed. Check its recorded result before trying again.")
export const searchStage = stage => ({ queued: "Queued", building: "Building replacement", verifying: "Verifying search", reconciling: "Applying account updates",
  activating: "Awaiting activation confirmation", completed: "Active index verified" }[stage] || "Waiting for confirmation")

export default function useSearchRebuild({ actorId, onSettled }) {
  const { state: { socket: { socket, socketClientId } = {} } = {} } = useContext(socketStore)
  const [state, setState] = useState({ status: "idle" })
  const active = useRef(null), settled = useRef(onSettled)
  settled.current = onSettled
  const key = "sportya-search-rebuild:" + actorId
  const observe = useCallback((operation, submit = false) => {
    active.current?.cancel()
    const watcher = { ended: false, reads: 0, failures: 0, sends: 0 }
    active.current = watcher
    const current = () => active.current === watcher && !watcher.ended
    const cleanup = () => { clearTimeout(watcher.timer); socket?.off("accounts-response", onSocket) }
    const poll = async () => {
      if (!current() || watcher.reading) return
      watcher.reading = true; watcher.reads += 1
      try {
        const response = await get(`/account-search/operations/${operation.operationId}`, { timeout: 5000 })
        const receipt = response?.data
        if (!current()) return
        if (receipt?.operationId !== operation.operationId || receipt.actorId !== actorId || !["pending", "completed", "failed"].includes(receipt.status)) throw new Error("Invalid receipt")
        watcher.failures = 0
        if (receipt.status === "pending") setState({ status: watcher.reads >= 20 ? "unknown" : "pending", receipt })
        else {
          watcher.ended = true; cleanup()
          try { sessionStorage.removeItem(key) } catch { /* Server history remains available. */ }
          setState({ status: receipt.status, receipt, message: receipt.status === "completed" ? "Account search index rebuilt and verified." : searchRebuildError(receipt.message) })
          settled.current?.(receipt)
        }
      } catch (error) {
        if (current() && [401, 403].includes(error.response?.status)) {
          watcher.ended = true; cleanup()
          setState({ status: "denied", message: searchRebuildError("SUPER_ADMIN_REQUIRED") })
        } else if (current() && ++watcher.failures >= 3) setState(value => ({ ...value, status: "unknown", message: "The result is not yet confirmed. It is safe to check or resume this same request." }))
      } finally {
        watcher.reading = false
        if (current()) { clearTimeout(watcher.timer); watcher.timer = setTimeout(poll, watcher.reads < 20 ? 1500 : 5000) }
      }
    }
    const onSocket = message => { if (message?.operation === "account-search-rebuild" && message.operationId === operation.operationId) void poll() }
    watcher.cancel = () => { watcher.ended = true; cleanup() }
    watcher.send = async () => {
      if (!current() || watcher.sending) return
      watcher.sending = true
      const first = ++watcher.sends === 1 && submit
      try {
        await post("/admin/account-search-rebuilds", { operationId: operation.operationId, reason: operation.reason, socketClientId },
          { timeout: 10000, headers: { "X-Correlation-Id": first ? operation.correlationId : newPlayerOperationId() } })
      } catch (error) {
        if (!current()) return
        if (first && [400, 401, 403, 404, 422].includes(error.response?.status)) {
          watcher.ended = true; cleanup()
          try { sessionStorage.removeItem(key) } catch { /* The request was not accepted. */ }
          const denied = [401, 403].includes(error.response.status)
          setState({ status: denied ? "denied" : "failed", message: searchRebuildError(denied ? "SUPER_ADMIN_REQUIRED" : "INVALID_SEARCH_REBUILD_REQUEST") })
        } else setState(value => ({ ...value, status: "unknown", message: "The result is not yet confirmed. It is safe to check or resume this same request." }))
      } finally { watcher.sending = false }
    }
    setState({ status: "pending", receipt: operation.receipt })
    socket?.on("accounts-response", onSocket)
    watcher.timer = setTimeout(poll, submit ? 500 : 0)
    if (submit) void watcher.send()
  }, [actorId, key, socket, socketClientId])
  useEffect(() => {
    setState({ status: "idle" })
    if (actorId) try {
      const stored = JSON.parse(sessionStorage.getItem(key) || "null")
      if (stored?.actorId === actorId && /^[a-zA-Z0-9-]{16,128}$/.test(stored.operationId || "") && typeof stored.reason === "string" && stored.correlationId) observe(stored)
    } catch { /* Local storage never authorizes a new request. */ }
    return () => { active.current?.cancel(); active.current = null }
  }, [actorId, key, observe])
  const resume = useCallback(receipt => {
    if (!actorId || receipt?.actorId !== actorId || (active.current && !active.current.ended)) return
    observe({ actorId, operationId: receipt.operationId, reason: receipt.reason, receipt })
  }, [actorId, observe])
  const start = reason => {
    if (!actorId || (active.current && !active.current.ended)) return false
    try {
      const operation = { actorId, operationId: newPlayerOperationId(), correlationId: newPlayerOperationId(), reason }
      sessionStorage.setItem(key, JSON.stringify(operation)); observe(operation, true); return true
    } catch { setState({ status: "failed", message: "Session storage is unavailable. Enable it so the rebuild can be recovered after reload." }); return false }
  }
  return { ...state, start, resume, pending: ["pending", "unknown"].includes(state.status), retry: () => active.current?.send?.() }
}
